/**
 * The head of the document: what it is, and between whom.
 *
 * The two parties are drawn as one block each, side by side from `sm` up and in
 * the order the PDF prints them - sender first, recipient second. The registry
 * and VAT numbers are shown for the same reason the paper carries them: an
 * accountant filing the document needs both, and a page that shows less than
 * the attachment only sends the reader back to the attachment.
 */
import { Badge, Card, CardContent } from "@wa/ui";
import { useT } from "../../i18n/I18nProvider";
import { genericStatusVariant } from "../../lib/format";
import type { PublicDoc } from "../../lib/publicDoc";
import { statusLabelKey } from "./lib";

function Party({ label, name, lines }: { label: string; name: string; lines: string[] }) {
  return (
    <div className="min-w-0">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1.5 text-sm font-semibold text-foreground">{name}</p>
      {lines.length > 0 ? (
        <div className="mt-1 space-y-0.5 text-sm text-muted-foreground">
          {lines.map((line) => (
            <p key={line} className="whitespace-pre-wrap break-words">{line}</p>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function DocParties({ doc }: { doc: PublicDoc }) {
  const t = useT();
  const s = doc.seller;
  // Built as a list rather than a fixed block so an empty field leaves no gap:
  // most senders fill in three of these five and the rest would print as blank
  // lines under the name.
  const sellerLines = [
    s.address,
    s.regCode ? `${t("publicDoc.regCode")}: ${s.regCode}` : "",
    s.vatNumber ? `${t("publicDoc.vatNumber")}: ${s.vatNumber}` : "",
    [s.email, s.phone, s.website].filter(Boolean).join(" · "),
  ].filter(Boolean);

  // The recipient, in the order the PDF prints them: address, registry number,
  // VAT number, email. Same list, same order, so a reader holding both does not
  // have to work out whether they are looking at the same document.
  //
  // `buyer` is absent on a response older than the block, and then the name is
  // all there is - which is exactly what this page showed before.
  const b = doc.buyer;
  const buyerLines = b
    ? [
        b.address,
        b.regCode ? `${t("publicDoc.regCode")}: ${b.regCode}` : "",
        b.vatNumber ? `${t("publicDoc.vatNumber")}: ${b.vatNumber}` : "",
        b.email ?? "",
      ].filter(Boolean)
    : [];

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-primary">
              {t(`publicDoc.kind.${doc.type}`)}
            </p>
            <p className="mt-0.5 text-xl font-semibold tracking-tight text-foreground">{doc.number}</p>
          </div>
          <Badge variant={genericStatusVariant(doc.status)}>{t(statusLabelKey(doc))}</Badge>
        </div>

        <div className="mt-5 grid gap-5 border-t border-border pt-5 sm:grid-cols-2">
          <Party label={t("publicDoc.sender")} name={s.name} lines={sellerLines} />
          <Party label={t("publicDoc.recipient")} name={b?.name ?? doc.clientName} lines={buyerLines} />
        </div>
      </CardContent>
    </Card>
  );
}
